import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { RuntimeModel } from "../runtime/modelCatalog";

export interface ImageModelOption {
  id: string;
  name: string;
  provider: string;
  description: string;
  badge?: string;
  supportsReferences?: boolean;
}

export const AVAILABLE_IMAGE_MODELS: [ImageModelOption, ...ImageModelOption[]] = [
  {
    id: "sana",
    name: "Sana",
    provider: "Replicate",
    description: "Linear DiT สปีดสูง ประมวลผลรวดเร็ว เหมาะกับการเรนเดอร์ภาพฉากทั่วไป",
    badge: "Fast Diffusion",
    supportsReferences: false,
  },
  {
    id: "gpt-image-2",
    name: "GPT Image 2",
    provider: "OpenAI",
    description: "โมเดลคุณภาพสูง อัตราความสอดคล้องตาม Prompt ละเอียด และรองรับ Style Reference",
    badge: "Reference Ready",
    supportsReferences: true,
  },
  {
    id: "gpt-image-2.5-flare",
    name: "GPT Image 2.5 Flare",
    provider: "OpenAI",
    description: "โมเดลหลักระดับพรีเมียมสำหรับสร้างภาพสถานที่ ทัศนียภาพ และแผนผังแสงเงาคมชัด",
    badge: "Location Primary",
    supportsReferences: true,
  },
  {
    id: "gpt-image-2.5-sunburst",
    name: "GPT Image 2.5 Sunburst",
    provider: "OpenAI",
    description: "โมเดลความละเอียดสูง โดดเด่นด้านมิติแสงธรรมชาติ รายละเอียดสถาปัตยกรรมและสีสันสดใส",
    badge: "Vivid Lighting",
    supportsReferences: true,
  },
  {
    id: "grok",
    name: "Grok Imagine",
    provider: "xAI",
    description: "โมเดลภาพสร้างสรรค์สูง คมชัด สไตล์จัดจ้าน เหมาะกับฉากแอ็กชัน ไซไฟ และแฟนตาซี",
    badge: "Creative High-Res",
    supportsReferences: true,
  },
];

export function ModelPicker({
  textOnly = false,
  models,
  selectedId,
  onSelect,
  selectedImageModelId = "gpt-image-2.5-flare",
  onSelectImageModel,
  imageGenerationEnabled = true,
  onToggleImageGeneration,
}: {
  textOnly?: boolean;
  models: RuntimeModel[];
  selectedId: string;
  onSelect: (model: RuntimeModel) => void;
  selectedImageModelId?: string;
  onSelectImageModel?: (modelId: string) => void;
  imageGenerationEnabled?: boolean;
  onToggleImageGeneration?: (enabled: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [modeTab, setModeTab] = useState<"text" | "image">("text");
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const dialogRef = useRef<HTMLDivElement>(null);

  const selected = models.find((m) => m.id === selectedId) ?? models[0];

  const currentImageModel = useMemo(() => {
    const aliases: Record<string, string> = { "sana-sprint-1.6b": "sana", "grok-imagine": "grok" };
    return AVAILABLE_IMAGE_MODELS.find(model => model.id === (aliases[selectedImageModelId] || selectedImageModelId)) ?? AVAILABLE_IMAGE_MODELS[0];
  }, [selectedImageModelId]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Filters like Craft: All, Core, Smartest, Gemini, DeepSeek, Claude, OpenAI
  const filteredModels = useMemo(() => {
    let list = models;
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (m) =>
          m.label.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q) ||
          m.group.toLowerCase().includes(q) ||
          (m.provider && m.provider.toLowerCase().includes(q))
      );
    }
    if (activeFilter === "core") {
      list = list.filter(
        (m) =>
          m.id.includes("flash") ||
          m.id.includes("deepseek") ||
          m.label.toLowerCase().includes("flash") ||
          (m.price?.inputUsdPerMillion ?? 0) <= 0.5
      );
    } else if (activeFilter === "smartest") {
      list = list.filter(
        (m) =>
          m.reasoning === "high" ||
          m.id.includes("pro") ||
          m.id.includes("opus") ||
          m.id.includes("o1") ||
          m.id.includes("o3") ||
          m.id.includes("r1")
      );
    } else if (activeFilter === "gemini") {
      list = list.filter((m) => m.id.includes("gemini") || m.provider?.toLowerCase().includes("google"));
    } else if (activeFilter === "deepseek") {
      list = list.filter((m) => m.id.includes("deepseek"));
    } else if (activeFilter === "claude") {
      list = list.filter((m) => m.id.includes("claude") || m.provider?.toLowerCase().includes("anthropic"));
    } else if (activeFilter === "openai") {
      list = list.filter((m) => m.id.includes("gpt") || m.provider?.toLowerCase().includes("openai"));
    }
    return list;
  }, [models, query, activeFilter]);

  const getIntelligenceScore = (m: RuntimeModel): number => {
    if (m.reasoning === "high" || m.id.includes("pro") || m.id.includes("opus") || m.id.includes("r1")) return 5;
    if (m.reasoning === "medium" || m.id.includes("sonnet") || m.id.includes("gpt-4o")) return 4;
    if (m.id.includes("flash") || m.id.includes("mini") || m.id.includes("haiku")) return 3;
    return 3;
  };

  return (
    <div className="model-picker">
      {/* Text and image model selections share one compact control. */}
      <button
        type="button"
        className="composer__model"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        title={textOnly ? "เลือกโมเดลข้อความของ Movis" : "เลือกโมเดลข้อความและโมเดลสร้างภาพ"}
      >
        <span className="model-pill-segment">
          <span className="model-pill-label">{selected?.label || selectedId || "เลือกโมเดล"}</span>
        </span>
        {!textOnly && <>
        <span className="model-pill-sep">·</span>
        <span className={`model-pill-segment ${!imageGenerationEnabled ? "is-off" : ""}`}>
          <span className="model-pill-label">
            {imageGenerationEnabled ? currentImageModel.name : "ปิดภาพ"}
          </span>
        </span>
        </>}
        <b className="model-pill-arrow">⌄</b>
      </button>

      {open && createPortal(
        <div className="model-modal-overlay" onClick={() => setOpen(false)}>
          <div
            className="model-modal-dialog"
            ref={dialogRef}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="เลือกโมเดล"
          >
            {/* Top Bar: Search + Mode Tabs [Text] [Image] */}
            <div className="model-modal-header">
              <div className="model-modal-search">
                <span className="search-icon">🔍</span>
                <input
                  autoFocus={modeTab === "text"}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={
                    modeTab === "text"
                      ? "ค้นหารุ่นโมเดล... (Search models)"
                      : "5 โมเดลสร้างภาพ (Sana, GPT Image 2, Flare, Sunburst, Grok)"
                  }
                  disabled={modeTab === "image"}
                />
                {query && modeTab === "text" && (
                  <button type="button" className="clear-btn" onClick={() => setQuery("")}>
                    ✕
                  </button>
                )}
              </div>
              <div className="model-modal-type-tabs">
                <button
                  type="button"
                  className={`type-pill ${modeTab === "text" ? "active" : ""}`}
                  onClick={() => setModeTab("text")}
                >
                  Text
                </button>
                {!textOnly &&                 <button
                  type="button"
                  className={`type-pill ${modeTab === "image" ? "active" : ""}`}
                  onClick={() => setModeTab("image")}
                >
                  Image
                </button>}
                <button
                  type="button"
                  className="model-modal-close"
                  onClick={() => setOpen(false)}
                  aria-label="ปิด"
                >
                  ✕
                </button>
              </div>
            </div>

            {modeTab === "text" ? (
              /* TAB 1: TEXT MODELS (Existing) */
              <>
                {/* Filter Chips (Like Craft: All, Core, Smartest, Gemini, DeepSeek...) */}
                <div className="model-modal-filters">
                  {[
                    { id: "all", label: "ทั้งหมด" },
                    { id: "core", label: "⚡ Core (ความเร็วสูง)" },
                    { id: "smartest", label: "🧠 Smartest (เหตุผลขั้นสูง)" },
                    { id: "deepseek", label: "DeepSeek" },
                    { id: "gemini", label: "Google Gemini" },
                    { id: "claude", label: "Claude" },
                    { id: "openai", label: "OpenAI GPT" },
                  ].map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={`filter-chip ${activeFilter === f.id ? "active" : ""}`}
                      onClick={() => setActiveFilter(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Table Columns Header */}
                <div className="model-modal-columns">
                  <span className="col-model">MODEL</span>
                  <span className="col-cost">COST & CONTEXT</span>
                  <span className="col-intel">INTELLIGENCE</span>
                </div>

                {/* Model List */}
                <div className="model-modal-list">
                  {filteredModels.length === 0 ? (
                    <div className="model-modal-empty">ไม่พบโมเดลที่ตรงกับคำค้นหา</div>
                  ) : (
                    filteredModels.map((m) => {
                      const isSelected = m.id === selectedId;
                      const intel = getIntelligenceScore(m);
                      const isCore =
                        m.id.includes("flash") ||
                        m.id.includes("deepseek") ||
                        (m.price?.inputUsdPerMillion ?? 0) <= 0.5;

                      return (
                        <button
                          key={m.id}
                          type="button"
                          className={`model-row ${isSelected ? "selected" : ""}`}
                          disabled={!m.available}
                          onClick={() => {
                            onSelect(m);
                            setOpen(false);
                          }}
                        >
                          {/* Left: Model Name & Description */}
                          <div className="model-row__name">
                            <div className="model-row__title-line">
                              <strong>{m.label}</strong>
                              {isSelected && <span className="model-active-badge">✓ ใช้งานอยู่</span>}
                              {isCore && <span className="model-core-badge">CORE</span>}
                            </div>
                            <small className="model-row__desc">
                              {m.context ? `${Math.round(m.context / 1000)}K context` : "context ตามระบบ"} ·{" "}
                              {m.group}
                            </small>
                          </div>

                          {/* Middle: Cost Info */}
                          <div className="model-row__cost">
                            {isCore ? (
                              <span className="cost-included">INCLUDED WITH PLAN</span>
                            ) : (
                              <span className="cost-rate">
                                ${m.price?.inputUsdPerMillion ?? "—"} / ${m.price?.outputUsdPerMillion ?? "—"} USD
                              </span>
                            )}
                            <small>
                              {isCore ? "ไม่คิดโควตาพิเศษ" : "ต่อ 1M tokens"}
                            </small>
                          </div>

                          {/* Right: Intelligence Pips */}
                          <div className="model-row__intel">
                            <div className="intel-pips" title={`ระดับความฉลาด: ${intel}/5`}>
                              {[1, 2, 3, 4, 5].map((pip) => (
                                <span
                                  key={pip}
                                  className={`pip ${pip <= intel ? "filled" : ""}`}
                                />
                              ))}
                            </div>
                            <small>{intel === 5 ? "สูงสุด" : intel === 4 ? "สูงมาก" : "มาตรฐาน"}</small>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                <div className="model-modal-footer">
                  <span>พร้อมใช้งาน {filteredModels.length} โมเดล (Gateway Router)</span>
                  <button type="button" className="footer-close-btn" onClick={() => setOpen(false)}>
                    ปิดหน้าต่าง
                  </button>
                </div>
              </>
            ) : (
              /* TAB 2: IMAGE MODELS (Craft RPG Style, 3 Models, No Cost System) */
              <div className="image-models-tab-content">
                {/* Image Generation Toggle */}
                <div className="image-gen-toggle-card">
                  <div className="image-gen-toggle-info">
                    <strong>Image generation</strong>
                    <small>Allow agents to create images (สร้างภาพประกอบตัวละคร สถานที่ ไอเทม และภารกิจอัตโนมัติ)</small>
                  </div>
                  <label className="image-toggle-switch">
                    <input
                      type="checkbox"
                      checked={imageGenerationEnabled}
                      onChange={(e) => onToggleImageGeneration?.(e.target.checked)}
                    />
                    <span className="image-toggle-slider" />
                  </label>
                </div>

                {/* Image Models List */}
                <div className="image-models-list">
                  {AVAILABLE_IMAGE_MODELS.map((img) => {
                    const isSelected = currentImageModel.id === img.id;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        className={`image-model-row ${isSelected ? "selected" : ""} ${!imageGenerationEnabled ? "disabled" : ""}`}
                        onClick={() => {
                          onSelectImageModel?.(img.id);
                        }}
                      >
                        <div className="image-model-row__main">
                          <div className="image-model-row__header">
                            <strong className="image-model-name">{img.name}</strong>
                            <span className="image-provider-badge">{img.provider}</span>
                            {img.badge && (
                              <span className="image-feature-badge">{img.badge}</span>
                            )}
                          </div>
                          <p className="image-model-desc">{img.description}</p>
                        </div>
                        <div className="image-model-row__action">
                          {isSelected ? (
                            <span className="image-model-selected-mark">✓ ใช้งานอยู่</span>
                          ) : (
                            <span className="image-model-select-action">เลือก</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Footer for Image tab */}
                <div className="model-modal-footer">
                  <span>โมเดลสร้างภาพพร้อมใช้งาน 5 โมเดล (Sana · GPT Image 2 · Flare · Sunburst · Grok)</span>
                  <button type="button" className="footer-close-btn" onClick={() => setOpen(false)}>
                    ปิดหน้าต่าง
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>, document.body
      )}
    </div>
  );
}
